/**
 * Person name fields: letters only (Unicode), spaces allowed between words.
 * No digits or punctuation — enforced via sanitize + validity check.
 *
 * FieldMax alignment (sender/receiver name fields):
 * - First name: 20
 * - Last name: 30
 * - Middle name (sender and receiver): 1
 */

// TODO: get these values from GFFP.
export const SENDER_FIRST_NAME_MAX_LENGTH = 20;
export const SENDER_LAST_NAME_MAX_LENGTH = 30;
export const SENDER_MIDDLE_NAME_MAX_LENGTH = 20;
export const RECEIVER_FIRST_NAME_MAX_LENGTH = 20;
export const RECEIVER_LAST_NAME_MAX_LENGTH = 30;
export const RECEIVER_MIDDLE_NAME_MAX_LENGTH = 20;

export function clampPersonNameToMaxLength(value: string, maxLen: number): string {
	if (value.length <= maxLen) return value;
	return value.slice(0, maxLen);
}

/** Removes any character that is not a Unicode letter or whitespace; optional max length (FieldMax). */
export function sanitizePersonNameInput(value: string, maxLen?: number): string {
	const cleaned = value.replace(/[^\p{L}\s]/gu, "");
	return maxLen !== undefined ? clampPersonNameToMaxLength(cleaned, maxLen) : cleaned;
}

/** One Unicode letter or whitespace only — bounded patterns, no `+` on full user string (ReDoS). */
function isLetterOrWhitespaceCodePoint(ch: string): boolean {
	return /^\s$/u.test(ch) || /^\p{L}$/u.test(ch);
}

function containsUnicodeLetter(value: string): boolean {
	for (const ch of value) {
		if (/^\p{L}$/u.test(ch)) return true;
	}
	return false;
}

/**
 * @param value Raw or sanitized value
 * @param required If true, trimmed value must contain at least one letter
 * @param maxLen If set, length must not exceed this (character count)
 */
export function isPersonNameSegmentValid(
	value: string,
	required: boolean,
	maxLen?: number
): boolean {
	const trimmed = value.trim();
	if (!trimmed) {
		return !required;
	}
	if (maxLen !== undefined && value.length > maxLen) {
		return false;
	}
	for (const ch of value) {
		if (!isLetterOrWhitespaceCodePoint(ch)) {
			return false;
		}
	}
	return containsUnicodeLetter(trimmed);
}

/** Truncate API- or session-provided names for display/store (no letter stripping). */
export function truncatePersonNameField(value: string, maxLen: number): string {
	const s = String(value ?? "");
	return s.length <= maxLen ? s : s.slice(0, maxLen);
}

/** City / locality: Unicode letters and spaces only (same as person names). */
export const sanitizeCityInput = sanitizePersonNameInput;

/**
 * US state field: ASCII letters only, uppercased. Allows typing a full state name for search
 * or a two-letter code; digits and symbols are stripped.
 */
export function sanitizeUsStateInput(value: string): string {
	return value.replace(/[^A-Za-z]/g, "").toUpperCase();
}
